class SRecommendProducts extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;

    this.btnPrev = null;
    this.btnNext = null;
    this.swiper = null;

    this.isRefreshing = false;
    this.refreshTimer = null;

    this.slidePrev = this.slidePrev.bind(this);
    this.slideNext = this.slideNext.bind(this);

    this.initialize();

    this.debouncedEqualize = this.debounce(() => {
      this.equalizeSlideHeights();
      this.swiper?.update();
    }, 100);

    window.addEventListener("resize", this.debouncedEqualize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.debouncedEqualize);

    this.btnPrev?.removeEventListener("click", this.slidePrev);
    this.btnNext?.removeEventListener("click", this.slideNext);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initialize() {
    const slides = this.querySelectorAll(".swiper-slide");

    if (slides.length > 0) {
      this.initializeSlider();
      return;
    }

    this.renderFallbackProducts();
  }

  renderFallbackProducts() {
    fetch(
      `${window.Shopify.routes.root}recommendations/products?product_id=${this.productId}&limit=20&section_id=${this.sectionId}&intent=related`
    )
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;

        const section = wrapper.querySelector("s-recommend-products");
        if (!section) return;

        const newList = section.querySelector(".s-recommend-products__list");
        const currentList = this.querySelector(".s-recommend-products__list");

        if (!newList || !currentList) return;

        currentList.innerHTML = newList.innerHTML;

        const slides = currentList.querySelectorAll(".swiper-slide");

        if (!slides.length) {
          this.closest(".section-recommend-products")?.remove();
          return;
        }

        this.initializeSlider();
      })
      .catch((error) => {
        console.warn("[SRecommendProducts] fallback failed:", error);
      });
  }

  refreshByVariant(variantId) {
    if (!variantId || !this.sectionId || this.isRefreshing) return;

    this.isRefreshing = true;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    url.searchParams.set("section_id", this.sectionId);

    fetch(url.toString(), {
      credentials: "same-origin"
    })
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;

        const newSection = wrapper.querySelector("s-recommend-products");
        if (!newSection) return;

        const newTitle = newSection.querySelector(".s-recommend-products__title");
        const currentTitle = this.querySelector(".s-recommend-products__title");

        const newList = newSection.querySelector(".s-recommend-products__list");
        const currentList = this.querySelector(".s-recommend-products__list");

        if (newTitle && currentTitle) {
          currentTitle.innerHTML = newTitle.innerHTML;
        }

        if (!newList || !currentList) return;

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        currentList.innerHTML = newList.innerHTML;

        const slides = currentList.querySelectorAll(".swiper-slide");

        if (!slides.length) {
          this.renderFallbackProducts();
          return;
        }

        this.initializeSlider();
      })
      .catch((error) => {
        console.warn("[SRecommendProducts] variant refresh failed:", error);
      })
      .finally(() => {
        this.isRefreshing = false;
      });
  }

  initializeSlider() {
    const slider = this.querySelector(".swiper");
    if (!slider || typeof Swiper === "undefined") return;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.swiper = new Swiper(slider, {
      slidesPerView: "auto",
      spaceBetween: 15,
      speed: 400,
      autoplay: {
        delay: 10000,
        disableOnInteraction: false
      },
      breakpoints: {
        992: {
          slidesPerView: 3,
          spaceBetween: 28
        }
      }
    });

    this.btnPrev = this.querySelector(".js-recommend-products__btn--prev");
    this.btnNext = this.querySelector(".js-recommend-products__btn--next");

    this.btnPrev?.removeEventListener("click", this.slidePrev);
    this.btnNext?.removeEventListener("click", this.slideNext);

    this.btnPrev?.addEventListener("click", this.slidePrev);
    this.btnNext?.addEventListener("click", this.slideNext);

    this.slideChange();

    this.swiper.on("slideChange", () => {
      this.slideChange();
    });

    this.swiper.on("resize", () => {
      this.equalizeSlideHeights();
    });

    setTimeout(() => {
      this.equalizeSlideHeights();
      this.swiper?.update();
    }, 150);
  }

  slidePrev() {
    this.swiper?.slidePrev();
  }

  slideNext() {
    this.swiper?.slideNext();
  }

  slideChange() {
    if (!this.swiper) return;

    if (this.swiper.isBeginning) {
      this.btnPrev?.setAttribute("disabled", "");
    } else {
      this.btnPrev?.removeAttribute("disabled");
    }

    if (this.swiper.isEnd) {
      this.btnNext?.setAttribute("disabled", "");
    } else {
      this.btnNext?.removeAttribute("disabled");
    }
  }

  equalizeSlideHeights() {
    const slides = this.querySelectorAll(".swiper-slide");

    if (!slides.length) return;

    let tallest = 0;

    slides.forEach((slide) => {
      slide.style.height = "auto";
    });

    slides.forEach((slide) => {
      tallest = Math.max(tallest, slide.offsetHeight);
    });

    slides.forEach((slide) => {
      slide.style.height = `${tallest}px`;
    });
  }

  debounce(fn, delay = 100) {
    let timer;

    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }
}

if (!customElements.get("s-recommend-products")) {
  customElements.define("s-recommend-products", SRecommendProducts);
}

document.addEventListener("change", (event) => {
  const target = event.target;

  if (!target.matches('.c-variant-picker__item input[type="radio"]')) return;

  const variantId = target.value;
  if (!variantId) return;

  document.querySelectorAll("s-recommend-products").forEach((section) => {
    if (section.refreshTimer) {
      clearTimeout(section.refreshTimer);
    }

    section.refreshTimer = setTimeout(() => {
      section.refreshByVariant?.(variantId);
    }, 80);
  });
});
